import { supabase } from "@/services/supabaseClient.js";

const REVIEW_CHANGED_EVENT = "afrimed:appointment-reviews:changed";

function toIntegerRating(value) {
  const next = Number(value);
  if (!Number.isFinite(next)) return null;
  const rounded = Math.round(next);
  if (rounded < 1 || rounded > 5) return null;
  return rounded;
}

function normalizeReview(row) {
  if (!row) return null;

  const doctorRating = toIntegerRating(row?.doctor_rating);
  const experienceRating = toIntegerRating(row?.experience_rating);
  const overallRaw = Number(row?.overall_rating);
  const overallRating =
    Number.isFinite(overallRaw) && overallRaw > 0
      ? Number(overallRaw).toFixed(1)
      : doctorRating && experienceRating
        ? ((doctorRating + experienceRating) / 2).toFixed(1)
        : "—";

  return {
    id: String(row?.id || "").trim(),
    appointmentId: String(row?.appointment_id || "").trim(),
    patientId: String(row?.patient_id || "").trim() || null,
    patientUserId: String(row?.patient_user_id || "").trim() || null,
    doctorUserId: String(row?.doctor_user_id || "").trim() || null,
    doctorRating,
    experienceRating,
    overallRating,
    reviewText: String(row?.review_text || "").trim(),
    languageCode: String(row?.language_code || "").trim() || "en-GB",
    createdAt: row?.created_at || null,
    updatedAt: row?.updated_at || null,
  };
}

function emitReviewChanged() {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new Event(REVIEW_CHANGED_EVENT));
  window.dispatchEvent(new Event("afrimed:appointments:changed"));
}

export function subscribeAppointmentDoctorReviews(listener) {
  if (typeof window === "undefined" || typeof listener !== "function") {
    return () => {};
  }

  window.addEventListener(REVIEW_CHANGED_EVENT, listener);
  return () => window.removeEventListener(REVIEW_CHANGED_EVENT, listener);
}

export async function loadMyAppointmentDoctorReview(appointmentId) {
  const reviewId = String(appointmentId || "").trim();
  if (!reviewId) return null;

  const { data, error } = await supabase
    .from("doctor_appointment_reviews")
    .select(
      "id,appointment_id,patient_id,patient_user_id,doctor_user_id,doctor_rating,experience_rating,overall_rating,review_text,language_code,created_at,updated_at"
    )
    .eq("appointment_id", reviewId)
    .maybeSingle();

  if (error) throw error;
  return normalizeReview(data);
}

export async function listMyAppointmentDoctorReviews(appointmentIds = []) {
  const ids = Array.from(
    new Set(
      (appointmentIds || [])
        .map((value) => String(value || "").trim())
        .filter(Boolean)
    )
  );

  if (ids.length === 0) return new Map();

  const { data, error } = await supabase
    .from("doctor_appointment_reviews")
    .select(
      "id,appointment_id,patient_id,patient_user_id,doctor_user_id,doctor_rating,experience_rating,overall_rating,review_text,language_code,created_at,updated_at"
    )
    .in("appointment_id", ids);

  if (error) throw error;

  return new Map(
    (Array.isArray(data) ? data : [])
      .map((row) => {
        const review = normalizeReview(row);
        return review?.appointmentId ? [review.appointmentId, review] : null;
      })
      .filter(Boolean)
  );
}

export async function saveMyAppointmentDoctorReview({
  appointmentId,
  doctorRating,
  experienceRating,
  reviewText = "",
  languageCode = "en-GB",
}) {
  const nextAppointmentId = String(appointmentId || "").trim();
  const nextDoctorRating = toIntegerRating(doctorRating);
  const nextExperienceRating = toIntegerRating(experienceRating);

  if (!nextAppointmentId) {
    throw new Error("Appointment review is missing its appointment.");
  }
  if (!nextDoctorRating) {
    throw new Error("Doctor rating must be between 1 and 5.");
  }
  if (!nextExperienceRating) {
    throw new Error("Experience rating must be between 1 and 5.");
  }

  const payload = {
    appointment_id: nextAppointmentId,
    doctor_rating: nextDoctorRating,
    experience_rating: nextExperienceRating,
    review_text: String(reviewText || "").trim() || null,
    language_code: String(languageCode || "").trim() || "en-GB",
  };

  const { data, error } = await supabase
    .from("doctor_appointment_reviews")
    .upsert(payload, { onConflict: "appointment_id" })
    .select(
      "id,appointment_id,patient_id,patient_user_id,doctor_user_id,doctor_rating,experience_rating,overall_rating,review_text,language_code,created_at,updated_at"
    )
    .single();

  if (error) throw error;

  const review = normalizeReview(data);
  let aggregate = null;

  if (review?.doctorUserId) {
    const { data: doctorProfile, error: doctorProfileError } = await supabase
      .from("doctor_profiles")
      .select("rating,review_count")
      .eq("user_id", review.doctorUserId)
      .maybeSingle();

    if (doctorProfileError) throw doctorProfileError;

    if (doctorProfile) {
      const ratingRaw = Number(doctorProfile?.rating);
      aggregate = {
        rating:
          Number.isFinite(ratingRaw) && ratingRaw > 0
            ? Number(ratingRaw).toFixed(1)
            : "—",
        reviewCount: Number(doctorProfile?.review_count || 0) || 0,
      };
    }
  }

  emitReviewChanged();

  return {
    review,
    aggregate,
  };
}
